var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import Schema from "async-validator";
import * as React from "react";
import { Button, Checkbox, Form, Input, InputNumber, Select, Switch, } from "antd";
import { Icon } from "@opendash/icons";
import ReactMarkdown from "react-markdown";
import { produce, useTranslation } from "..";
// Avoid warnings from the validator in the console
// @ts-ignore
Schema.warning = () => { };
export const FormGenerator = ({ elements, state: externalState, updateState: externalUpdateState, onChange, submit: submitOptions, onSubmit, settings, children, }) => {
    const t = useTranslation();
    const firstRunRef = React.useRef(true);
    const dirtyRef = React.useRef({});
    const [internalState, setInternalState] = React.useState({});
    const [errorState, setErrorState] = React.useState({});
    const schema = React.useMemo(() => {
        return new Schema(Object.fromEntries(elements
            .filter((field) => field.rules)
            .map((field) => [field.key, field.rules])));
    }, [elements]);
    const hasExternalState = externalState && externalUpdateState;
    const state = hasExternalState ? externalState : internalState;
    const updateStateHandler = React.useCallback((key, name, value) => {
        name = name || key;
        const nextState = produce(state, (draft) => {
            setSelector(draft, name, value);
        });
        if (hasExternalState) {
            const x = (Array.isArray(name) ? name[0] : name).toString();
            externalUpdateState(x, nextState[x]);
        }
        else {
            setInternalState(nextState);
        }
        if (!dirtyRef.current[key]) {
            dirtyRef.current[key] = true;
        }
    }, [state, hasExternalState, externalUpdateState]);
    const visibleElements = elements.filter((field) => {
        if (field.visible === false) {
            return false;
        }
        else if (typeof field.visible === "function") {
            return field.visible(state);
        }
        else {
            return true;
        }
    });
    const invisibleElements = elements.filter((field) => {
        if (field.visible === true) {
            return true;
        }
        else if (typeof field.visible === "function") {
            return !field.visible(state);
        }
        else {
            return false;
        }
    });
    React.useEffect(() => {
        if (!firstRunRef.current) {
            if (onChange) {
                onChange(state);
            }
            // @ts-ignore
            schema.validate(state, {}, (errors) => {
                if (errors) {
                    setErrorState(Object.fromEntries(errors.map((error) => [error.field, error.message])));
                }
                else {
                    setErrorState({});
                }
            });
        }
        firstRunRef.current = false;
        for (const field of visibleElements) {
            if (field.defaultValue !== undefined &&
                (field.key in state === false || state[field.key] === undefined)) {
                updateStateHandler(field.key, field.name, field.defaultValue);
            }
        }
        if (settings === null || settings === void 0 ? void 0 : settings.removeHidden) {
            for (const field of invisibleElements) {
                if (field.key in state === true && state[field.key] !== undefined) {
                    updateStateHandler(field.key, field.name, undefined);
                }
            }
        }
    }, [state]);
    const layout = (settings === null || settings === void 0 ? void 0 : settings.layout) ? settings.layout : "vertical";
    return (React.createElement(Form, { layout: layout, onFinish: () => {
            if (onSubmit && !Object.values(errorState).some((e) => e)) {
                onSubmit(state);
            }
        } },
        visibleElements.map((field) => {
            return (React.createElement(Form.Item, { key: field.key, label: !(settings === null || settings === void 0 ? void 0 : settings.hideLabels) && t(field.label), 
                // hasFeedback
                validateStatus: dirtyRef.current[field.key]
                    ? errorState[field.key]
                        ? "error"
                        : "success"
                    : undefined, help: dirtyRef.current[field.key] && errorState[field.key] ? (errorState[field.key]) : field.descriptionMarkdown ? (React.createElement(ReactMarkdown, { children: field.descriptionMarkdown })) : field.description ? (t(field.description)) : undefined, tooltip: field.hintMarkdown ? (React.createElement(ReactMarkdown, { children: field.hintMarkdown })) : (t(field.hint)) },
                React.createElement(FormGeneratorField, { field: field, value: getSelector(state, field.name || field.key), setValue: (v) => {
                        updateStateHandler(field.key, field.name, v);
                    } })));
        }),
        typeof children !== "undefined" ? children : null,
        submitOptions && (React.createElement(Form.Item, null,
            React.createElement(Button, Object.assign({ type: "primary", htmlType: "submit", disabled: Object.values(errorState).some((e) => e) }, (submitOptions || {}))))),
        !submitOptions && (React.createElement("div", { style: { display: "hidden" } }))));
};
const FormGeneratorField = ({ field, value, setValue, }) => {
    var _a, _b, _c, _d, _e, _f;
    const t = useTranslation();
    if (field.type === "input") {
        const _g = field.settings || {}, { prefixIcon } = _g, settings = __rest(_g, ["prefixIcon"]);
        if (prefixIcon) {
            settings.prefix = (React.createElement(Icon, { icon: prefixIcon, style: { color: "rgba(0,0,0,.25)" } }));
        }
        return (React.createElement(Input, Object.assign({}, settings, { value: value, onChange: (e) => setValue(e.target.value), type: settings.type || "text", style: field.style })));
    }
    if (field.type === "input.tags") {
        const seperator = ((_a = field.settings) === null || _a === void 0 ? void 0 : _a.seperator) || ",";
        return (React.createElement(Select, { mode: "tags", tokenSeparators: [seperator], value: value ? value.split(seperator) : [], onChange: (nextValue) => setValue(nextValue.join(seperator)), style: field.style, notFoundContent: null }));
    }
    if (field.type === "input.password") {
        const _h = field.settings || {}, { prefixIcon } = _h, settings = __rest(_h, ["prefixIcon"]);
        if (prefixIcon) {
            settings.prefix = (React.createElement(Icon, { icon: prefixIcon, style: { color: "rgba(0,0,0,.25)" } }));
        }
        return (React.createElement(Input.Password, { value: value, onChange: (e) => setValue(e.target.value), placeholder: ((_b = field.settings) === null || _b === void 0 ? void 0 : _b.placeholder) || void 0, prefix: ((_c = field.settings) === null || _c === void 0 ? void 0 : _c.prefix) || null, style: field.style }));
    }
    if (field.type === "input.number") {
        return (React.createElement(InputNumber, Object.assign({ value: value, onChange: (nextValue) => setValue(nextValue), style: field.style }, field.settings)));
    }
    if (field.type === "switch") {
        return (React.createElement(Switch, { checked: value, onChange: (nextValue) => {
                setValue(nextValue);
            }, style: field.style }));
    }
    if (field.type === "checkbox") {
        return (React.createElement(Checkbox, { checked: value, onChange: (e) => {
                setValue(e.target.checked);
            }, style: field.style }));
    }
    if (field.type === "textarea") {
        return (React.createElement(Input.TextArea, { value: value, onChange: (e) => setValue(e.target.value), rows: ((_d = field.settings) === null || _d === void 0 ? void 0 : _d.rows) || 4, style: field.style }));
    }
    if (field.type === "select") {
        return (React.createElement(Select, { placeholder: (_e = field.settings) === null || _e === void 0 ? void 0 : _e.placeholder, value: value, onChange: (v) => {
                setValue(v);
            }, style: field.style }, (_f = field.settings) === null || _f === void 0 ? void 0 : _f.options.map((option) => {
            return (React.createElement(Select.Option, { key: option.value, value: option.value }, t(option.label)));
        })));
    }
    // if (field.type === "select-item") {
    // return (
    //   <DataItemPicker
    //     value={value}
    //     onChange={(v) => setValue(v)}
    //     style={field.style}
    //   />
    // );
    // }
    console.warn(`FormGenerator: Type '${field.type}' does not exist.`);
    return null;
};
function getSelector(obj, path) {
    try {
        let value = obj;
        if (Array.isArray(path)) {
            for (const key of path) {
                value = value[key];
            }
        }
        else {
            value = value[path];
        }
        return value;
    }
    catch (error) {
        return null;
    }
}
function setSelector(obj, path, value) {
    try {
        if (!Array.isArray(path)) {
            obj[path] = value;
            return true;
        }
        if (path.length === 1) {
            return setSelector(obj, path[0], value);
        }
        const [key, nextKey, ...remainingSelector] = path;
        if (!(key in obj)) {
            if (Number.isInteger(nextKey)) {
                obj[key] = [];
            }
            else {
                obj[key] = {};
            }
        }
        return setSelector(obj[key], [nextKey, ...remainingSelector], value);
    }
    catch (error) {
        return false;
    }
}
//# sourceMappingURL=FormGenerator.js.map